import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { en } from './locales/en';
import { th } from './locales/th';

export const languageStorageKey = 'affiliate.language';
export const supportedLanguages = ['th', 'en'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

const defaultLanguage: SupportedLanguage = 'th';

export function readStoredLanguage(storage: Pick<Storage, 'getItem'> | null): SupportedLanguage {
  if (storage === null) return defaultLanguage;
  try {
    const stored = storage.getItem(languageStorageKey);
    return isSupportedLanguage(stored) ? stored : defaultLanguage;
  } catch {
    return defaultLanguage;
  }
}

void i18n.use(initReactI18next).init({
  fallbackLng: 'en',
  initAsync: false,
  interpolation: { escapeValue: false },
  lng: readStoredLanguage(browserStorage()),
  returnNull: false,
  resources: {
    en: { translation: en },
    th: { translation: th },
  },
  supportedLngs: [...supportedLanguages],
});

i18n.on('languageChanged', (language) => {
  persistLanguage(language);
  syncDocumentLanguage(language);
});

syncDocumentLanguage(i18n.language);

export default i18n;

function isSupportedLanguage(value: string | null): value is SupportedLanguage {
  return value !== null && supportedLanguages.includes(value as SupportedLanguage);
}

function browserStorage(): Pick<Storage, 'getItem'> | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function persistLanguage(language: string): void {
  if (!isSupportedLanguage(language)) return;
  try {
    window.localStorage.setItem(languageStorageKey, language);
  } catch {
    // A browser with storage disabled still gets a working language switch for this session.
  }
}

function syncDocumentLanguage(language: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language.split('-', 1)[0] ?? defaultLanguage;
}
